import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import abatinaLogo from "@/assets/logo-abatina.webp";
import hero from "@/assets/hero.webp";
import { Reveal } from "@/components/Reveal";
import { SafeImage } from "@/components/SafeImage";
import { Eyebrow } from "@/components/Eyebrow";
import { SectionTitle } from "@/components/SectionTitle";
import { ArrowRight } from "lucide-react";
import { listPublicProducts } from "@/lib/public.functions";
import { formatPrice, type Product } from "@/data/products";
import { useProductsHidden } from "@/hooks/useSiteSettings";
import paneEAbatina from "@/assets/pane-e-abatina.webp";
import paninoAbatina from "@/assets/panino-abatina.webp";
import pastaRagu from "@/assets/pasta-ragu.webp";
import abatinaSeccaImg from "@/assets/abatina-secca.webp";
import abatinaFrescaImg from "@/assets/abatina-fresca.webp";

export const Route = createFileRoute("/abatina")({
  head: () => ({
    meta: [
      { title: "L'Abatina — Salume Slow Food di Campobasso | La Bottega delle Carni" },
      { name: "description", content: "L'Abatina, salume storico di Campobasso riconosciuto da Slow Food Arca del Gusto. Prodotto nelle varianti secca e fresca dalla Bottega delle Carni da Marco." },
      { property: "og:title", content: "L'Abatina — il salume Slow Food di Campobasso" },
      { property: "og:description", content: "Storia, varianti e produzione del nostro salume presidio Slow Food." },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://labottegadellecarnidamarco.it/abatina" },
    ],
    links: [{ rel: "canonical", href: "https://labottegadellecarnidamarco.it/abatina" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org", "@type": "Product",
        name: "L'Abatina",
        description: "Salume tradizionale molisano riconosciuto da Slow Food Arca del Gusto, prodotto nelle varianti secca e fresca.",
        brand: { "@type": "Brand", name: "La Bottega delle Carni da Marco" },
      }),
    }],
  }),
  component: AbatinaPage,
});

const ROADMAP = [
  { year: "1940", title: "L'invenzione", text: "Nasce la ricetta originale presso la Chiesa di Sant'Antonio Abate, a Campobasso." },
  { year: "Anni '60", title: "La diffusione", text: "I norcini molisani iniziano a tramandare la ricetta." },
  { year: "2010 ca.", title: "Slow Food", text: "L'Abatina entra nell'Arca del Gusto." },
  { year: "Oggi", title: "La nostra produzione", text: "Produciamo le varianti secca e fresca seguendo la tradizione." },
  { year: "2025", title: "De.Co. Campobasso", text: "L'Abatina viene ufficialmente iscritta nel registro Denominazione Comunale di Campobasso, insieme alla Soppressata casereccia e all'acciaio traforato. La notizia è ripresa dall'agenzia ANSA." },
];

function Variant({ id, side, title, eyebrow, paragraphs, specs, product, fallbackImage }: { id: string; side: "left" | "right"; title: string; eyebrow: string; paragraphs: string[]; specs: { label: string; value: string }[]; product?: Product; fallbackImage?: string }) {
  // Con il catalogo spento la scheda prodotto rimanda in home: il pulsante non va mostrato.
  const productsHidden = useProductsHidden();
  const image = product?.image || fallbackImage;
  const effectiveSpecs = (product?.specs && product.specs.length > 0) ? product.specs : specs;
  const img = <div className="aspect-square overflow-hidden rounded-md zoom-img-parent"><SafeImage src={image} alt={title} className="zoom-img w-full h-full object-cover"/></div>;
  const info = (
    <div>
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="font-display text-3xl md:text-4xl mt-3">{title}</h2>
      <div className="mt-5 space-y-4 text-brand-warm-gray leading-[1.8]">
        {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
      </div>
      {effectiveSpecs.length > 0 && (
        <dl className="mt-6 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          {effectiveSpecs.map((s, i) => (
            <div key={`${s.label}-${i}`} className="border-b border-brand-charcoal/10 py-2">
              {s.label && <dt className="text-xs uppercase tracking-wider text-brand-warm-gray">{s.label}</dt>}
              <dd className="text-brand-charcoal mt-0.5">{s.value}</dd>
            </div>
          ))}
        </dl>
      )}
      <div className="mt-6 flex items-center gap-4">
        <div className="font-numeric font-bold text-brand-red text-2xl">
          {product ? formatPrice(product) : null}
        </div>
        {!productsHidden && (
          <Link to="/prodotti/$slug" params={{ slug: id === "secca" ? "abatina-secca" : "abatina-fresca" }} className="bg-brand-red hover:bg-brand-red-hover text-white px-5 py-3 rounded-md text-sm uppercase tracking-[0.16em]">
            Scopri e richiedi
          </Link>
        )}
      </div>
    </div>
  );
  return (
    <section id={id} className="py-20">
      <div className="container mx-auto px-4 grid md:grid-cols-5 gap-10">
        {side === "left" ? (<><div className="md:col-span-3">{img}</div><div className="md:col-span-2">{info}</div></>) : (<><div className="md:col-span-2">{info}</div><div className="md:col-span-3">{img}</div></>)}
      </div>
    </section>
  );
}

function AbatinaPage() {
  const { data } = useQuery({ queryKey: ["public-products"], queryFn: () => listPublicProducts() });
  const products = data?.products ?? [];
  const abatinaSecca = products.find((p) => p.slug === "abatina-secca");
  const abatinaFresca = products.find((p) => p.slug === "abatina-fresca");
  return (
    <>
      <section className="relative min-h-[85vh] flex items-center justify-center pt-20 paper-texture overflow-hidden bg-brand-charcoal">
        <img src={hero} alt="" className="absolute inset-0 w-full h-full object-cover object-top opacity-30"/>
        <div className="absolute inset-0 bg-gradient-to-b from-brand-charcoal/85 via-brand-charcoal/70 to-brand-charcoal"/>
        <div className="container mx-auto px-4 text-center relative flex flex-col items-center">
          <h1 className="contents">
            <img src={abatinaLogo} alt="L'Abatina — il salume storico di Campobasso" width={1000} height={554} fetchPriority="high" decoding="async" className="h-[17.25rem] sm:h-[20.7rem] md:h-[27.6rem] w-auto max-w-[85vw] mx-auto drop-shadow-[0_8px_30px_rgba(0,0,0,0.5)]"/>
            <span className="sr-only">L'Abatina: il salume storico di Campobasso</span>
          </h1>
          <p className="font-italic-serif italic text-brand-gold text-2xl md:text-3xl mt-8">Il boccone divino dell'abate</p>
          <p className="text-white/85 mt-5 max-w-2xl mx-auto leading-relaxed">
            Salume storico di Campobasso, inventato nel 1940 presso la Chiesa di Sant'Antonio Abate. Oggi custodito da pochi norcini molisani che ne tramandano la ricetta segreta.
          </p>
        </div>
      </section>

      <section className="py-20 bg-brand-cream">
        <div className="container mx-auto px-4 grid md:grid-cols-5 gap-10">
          <Reveal className="md:col-span-3">
            <Eyebrow>La storia</Eyebrow>
            <SectionTitle className="mt-3">Il salume nato dal silenzio di un abate</SectionTitle>
            <div className="mt-6 space-y-4 text-brand-warm-gray leading-[1.85]">
              <p>1940, Campobasso. Presso la Chiesa di Sant'Antonio Abate vive don Annibale Ricciuti, un abate dalla salute delicata che non sopporta l'odore intenso dei salumi tradizionali di maiale.</p>
              <p>La sua perpetua, donna ingegnosa e cuoca formidabile, inventa una ricetta tutta nuova: macina finemente la carne, la pressa, la profuma con una miscela segreta di spezie. Nasce così il "boccone divino dell'abate".</p>
              <p>Negli anni il salume entra nella tradizione norcina molisana: pochi produttori ne custodiscono la ricetta, tramandandola di generazione in generazione.</p>
              <p>Oggi L'Abatina è ufficialmente riconosciuta da Slow Food come presidio dell'Arca del Gusto, il catalogo che protegge i prodotti agroalimentari tradizionali a rischio di estinzione.</p>
              <p>La Bottega delle Carni da Marco ne è custode e produttore: la lavoriamo nelle due varianti — secca e fresca — seguendo la ricetta originale.</p>
            </div>
          </Reveal>

          <Reveal className="md:col-span-2" delay={0.15}>
            <Eyebrow>Roadmap</Eyebrow>
            <h3 className="font-display text-2xl mt-3 mb-6">Le tappe</h3>
            <ol className="relative border-l-2 border-brand-gold/40 pl-6 space-y-6">
              {ROADMAP.map((m) => (
                <li key={m.year} className="relative">
                  <span className="absolute -left-[31px] top-1 w-4 h-4 rounded-full bg-brand-red border-4 border-brand-cream"/>
                  <div className="font-numeric font-semibold text-brand-red text-sm">{m.year}</div>
                  <div className="font-display text-lg mt-0.5">{m.title}</div>
                  <p className="text-sm text-brand-warm-gray mt-1">{m.text}</p>
                </li>
              ))}
            </ol>
          </Reveal>
        </div>
      </section>

      <Variant
        id="secca" side="left"
        product={abatinaSecca}
        fallbackImage={abatinaSeccaImg}
        eyebrow="L'Abatina Secca"
        title="La tradizione stagionata"
        paragraphs={[
          "L'Abatina Secca è la versione classica, stagionata all'aria aperta per alcune settimane. Il colore profondo, il sapore intenso, la consistenza compatta: è così che si presenta dopo la stagionatura tradizionale.",
          "Dopo la pressatura iniziale di 3 giorni, ogni Abatina viene appesa nella nostra stagionatura naturale, dove tempo e clima fanno il loro lavoro.",
          "Nessun additivo, nessun conservante: solo carne di maiale macinata finemente, sale e una miscela segreta di spezie tra cui spicca il peperoncino.",
        ]}
        specs={[
          { label: "Stagionatura", value: "15 giorni" },
          { label: "Forma", value: "Schiacciata" },
          { label: "Conservazione", value: "Ambiente fresco" },
          { label: "Consumo", value: "Affettata sottile" },
        ]}
      />

      <Variant
        id="fresca" side="right"
        product={abatinaFresca}
        fallbackImage={abatinaFrescaImg}
        eyebrow="L'Abatina Fresca"
        title="Il sapore appena pressato"
        paragraphs={[
          "L'Abatina Fresca è la versione giovane, consumata pochi giorni dopo la pressatura. Mantiene tutta la morbidezza e la freschezza degli aromi appena lavorati, con un sapore più delicato rispetto alla versione stagionata.",
          "La pressatura di 3 giorni conferisce la tipica forma schiacciata, mentre l'assenza di stagionatura lunga la rende perfetta per essere cucinata: in padella, alla griglia, o aggiunta a sughi e ragù per un sapore intenso e profumato.",
          "È la versione più amata da chi cerca un'Abatina morbida e versatile in cucina.",
        ]}
        specs={[
          { label: "Dimensioni", value: "7x14" },
          { label: "Forma", value: "Schiacciata" },
          { label: "Conservazione", value: "Frigo, max 7 giorni" },
          { label: "Consumo", value: "Cotta in padella o ragù" },
        ]}
      />

      <section className="bg-brand-cream py-20">
        <div className="container mx-auto px-4 text-center max-w-4xl">
          <Eyebrow>Abbinamenti</Eyebrow>
          <SectionTitle align="center" className="mt-3">Come gustare al meglio L'Abatina</SectionTitle>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              { t: "Sull'affettato", d: "Tagliata sottile e accompagnata da pane casereccio, è perfetta come antipasto.", img: paneEAbatina },
              { t: "Nel panino", d: "Con peperoni arrostiti o friarielli saltati: un classico campobassano.", img: paninoAbatina },
              { t: "Pasta al ragù", d: "Sbriciolata nel sugo, L'Abatina Fresca regala un ragù profumato e cremoso: perfetto sui cavatelli fatti a mano." , img: pastaRagu },
            ].map((c) => (
              <div key={c.t} className="bg-white p-6 rounded-md text-left">
                <div className="aspect-[4/3] bg-brand-cream-dark rounded-sm mb-4 overflow-hidden">
                  <img src={c.img} alt={c.t} loading="lazy" decoding="async" className="w-full h-full object-cover"/>
                </div>
                <h3 className="font-display text-xl">{c.t}</h3>
                <p className="text-sm text-brand-warm-gray mt-2">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-charcoal text-white py-20">
        <div className="container mx-auto px-4 grid md:grid-cols-2 gap-10 items-center">
          <div className="flex justify-center md:justify-end">
            <div className="inline-flex items-center justify-center w-52 h-52 rounded-full bg-brand-cream border-4 border-brand-gold/50 p-6">
              <img
                src="/certifications/slow-food.webp"
                alt="Slow Food — Arca del Gusto"
                className="w-full h-full object-contain"
                style={{ mixBlendMode: "multiply" }}
              />
            </div>
          </div>
          <div>
            <Eyebrow>Slow Food Arca del Gusto</Eyebrow>
            <h3 className="font-display text-white text-3xl mt-3">Un patrimonio da custodire</h3>
            <p className="text-brand-cream/85 mt-4 leading-relaxed">
              L'Abatina è ufficialmente inserita nell'Arca del Gusto, il catalogo che la Fondazione Slow Food dedica ai prodotti agroalimentari tradizionali a rischio di estinzione. Produrla significa preservare la biodiversità gastronomica del Molise.
            </p>
            <a href="https://www.fondazioneslowfood.com/it/arca-del-gusto-slow-food/abatina/" target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 border border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-brand-charcoal px-5 py-3 rounded-md text-sm uppercase tracking-[0.16em] transition-colors">
              Scopri Slow Food <ArrowRight size={14}/>
            </a>
          </div>
        </div>
      </section>

      <section className="bg-brand-red text-white py-16 text-center">
        <div className="container mx-auto px-4">
          <h2 className="font-display text-white text-3xl md:text-4xl">Vuoi assaggiare L'Abatina?</h2>
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <Link to="/contatti" className="bg-white text-brand-red px-6 py-3 rounded-md text-sm uppercase tracking-[0.16em] hover:bg-brand-cream">Vienici a trovare</Link>
            <Link to="/prodotti/$slug" params={{ slug: "abatina-secca" }} className="border border-white/60 hover:bg-white/10 px-6 py-3 rounded-md text-sm uppercase tracking-[0.16em]">Richiedi una porzione</Link>
          </div>
        </div>
      </section>
    </>
  );
}
